import { useState } from 'react';
import { Download, FileStack } from 'lucide-react';
import { Badge, Button, Card, EmptyState, Skeleton, Table, cn, useToast, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { ApiClientError } from '../../../shared/api/client.js';
import { downloadBlob } from '../../../shared/api/download.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Select } from '../../../shared/ui/Select.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { useOrgUnits } from '../../org/model/org.queries.js';
import { requisitionErrorText } from '../../requisition/model/requisition-error.js';
import {
  accountingApi,
  docFileName,
  type IncomingDocQuery,
  type IncomingDocRowDto,
} from '../api/accounting.api.js';
import { useIncomingDocs } from '../model/accounting.queries.js';
import { formatDate } from '../model/format.js';
import { DocPreviewDialog } from './DocPreviewDialog.js';
import { IncomingDocCards, IncomingDocCardsSkeleton } from './IncomingDocCards.js';

/**
 * Prixod hujjatlari — KORXONA hisobchisining ekrani.
 *
 * Doirani backend qo'yadi: `accounting.doc.read` odatda korxona doirasida
 * beriladi, hujjat esa `org_unit_id` bilan biriktirilgan — hisobchi begona
 * korxonaning prixodini ro'yxatda umuman ko'rmaydi.
 *
 * Filtr faqat SHARTNOMADA bor maydonlardan yig'iladi
 * (`incomingDocQuerySchema`: `orgUnitId`, `requisitionId`). Sana oralig'i
 * qo'shilmadi — uni server qabul qilmaydi va ekranda ishlamaydigan maydon
 * ko'rsatish yo'qolgan ishonchdan boshqa narsa bermaydi.
 */

const EMPTY_ORG_UNIT = '';

export const IncomingDocsPage = () => {
  const toast = useToast();
  const [orgUnitId, setOrgUnitId] = useState<string>(EMPTY_ORG_UNIT);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [pdfPendingId, setPdfPendingId] = useState<string | null>(null);

  const query: IncomingDocQuery = {
    ...(orgUnitId === EMPTY_ORG_UNIT ? {} : { orgUnitId }),
    limit: 50,
  };
  const list = useIncomingDocs(query);

  const orgUnits = useOrgUnits();

  const items = list.data?.items ?? [];

  /* Keng ekranda jadval, torda kartochkalar — chegara ilova bilan bitta
     (1024px, `DESKTOP_QUERY`). */
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const openDoc = (row: IncomingDocRowDto): void => setPreviewId(row.id);

  /**
   * PDF `apiBlob` bilan olinadi va `objectURL` orqali saqlanadi: imzolangan
   * havola ochilsa tokenni manzilga yozishga to'g'ri kelardi va u brauzer
   * tarixida qolib ketardi.
   */
  const downloadPdf = (docId: string, docNo: string): void => {
    setPdfPendingId(docId);
    accountingApi
      .docPdf(docId)
      .then((blob) => {
        downloadBlob(blob, docFileName(docNo));
        toast.show(t('accounting.pdfDone', { docNo }), 'success');
      })
      .catch((error: unknown) => {
        toast.show(
          error instanceof ApiClientError ? error.message : t('accounting.pdfFailed'),
          'danger',
        );
      })
      .finally(() => setPdfPendingId(null));
  };

  const columns: Column<IncomingDocRowDto>[] = [
    {
      key: 'docNo',
      header: t('accounting.colDocNo'),
      mono: true,
      width: '10rem',
      render: (row) => <span className="font-semibold text-text">{row.docNo}</span>,
    },
    {
      key: 'requisitionDocNo',
      header: t('accounting.fieldRequisition'),
      mono: true,
      width: '10rem',
    },
    {
      key: 'orgUnitId',
      header: t('accounting.fieldOrgUnit'),
      render: (row) =>
        row.orgUnitId === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          (orgUnits.nameOf(row.orgUnitId) ?? `#${row.orgUnitId}`)
        ),
    },
    {
      key: 'createdAt',
      header: t('accounting.colDate'),
      mono: true,
      width: '8rem',
      render: (row) => formatDate(row.createdAt) ?? '—',
    },
    {
      key: 'externalRef',
      header: t('accounting.colExternalRef'),
      width: '9rem',
      // 2-etap: 1C ga uzatilganda tashqi havola shu ustunda ko'rinadi.
      // Hozircha hammasi "uzatilmagan" — buni yashirish o'rniga ochiq
      // ko'rsatamiz: hisobchi qaysi hujjat hali ketmaganini bilishi kerak.
      render: (row) =>
        row.externalRef === null ? (
          <Badge tone="neutral">{t('accounting.notSentTo1c')}</Badge>
        ) : (
          <Badge tone="success" mono>
            {row.externalRef}
          </Badge>
        ),
    },
    {
      key: 'pdf',
      header: t('accounting.downloadPdf'),
      align: 'right',
      width: '7rem',
      render: (row) => (
        <Button
          variant="secondary"
          size="sm"
          aria-label={t('accounting.downloadPdfOf', { docNo: row.docNo })}
          loading={pdfPendingId === row.id}
          onClick={(event) => {
            // Qator bosilishi oynani ochadi — tugma uni tortib olmasligi kerak.
            event.stopPropagation();
            downloadPdf(row.id, row.docNo);
          }}
        >
          <Download size={14} aria-hidden />
          {t('accounting.downloadPdf')}
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('accounting.docsTitle')}
        description={t('accounting.docsSubtitle')}
        filtersLabel={t('accounting.filtersLabel')}
        filters={
          <>
            <Select
              variant="filter"
              label={t('accounting.filterOrgUnit')}
              placeholder={t('accounting.filterAllOrgUnits')}
              value={orgUnitId}
              onChange={(event) => setOrgUnitId(event.target.value)}
              options={orgUnits.items.map((unit) => ({ value: unit.id, label: unit.name }))}
            />
            <Button
              variant="ghost"
              disabled={orgUnitId === EMPTY_ORG_UNIT}
              onClick={() => setOrgUnitId(EMPTY_ORG_UNIT)}
            >
              {t('accounting.filterReset')}
            </Button>
          </>
        }
      />

      {list.isPending ? (
        desktop ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <IncomingDocCardsSkeleton />
        )
      ) : list.isError ? (
        <Card>
          <EmptyState
            title={t('accounting.listError')}
            description={requisitionErrorText(list.error, '')}
            action={
              <Button variant="secondary" onClick={() => void list.refetch()}>
                {t('accounting.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileStack size={20} aria-hidden />}
            title={t('accounting.docsEmpty')}
            description={t('accounting.docsEmptyHint')}
          />
        </Card>
      ) : (
        <>
          {/* Faqat KERAKLISI chiziladi: ikkalasi ham DOM da tursa, ekran
              o'qigich bitta ro'yxatni ikki marta eshitardi. Ma'lumot,
              ustunlar va bosish amali — ikkalasi uchun bitta. */}
          {desktop ? (
            <Table
              caption={t('accounting.docsCaption')}
              columns={columns}
              rows={items}
              rowKey={(row) => row.id}
              onRowClick={openDoc}
            />
          ) : (
            <IncomingDocCards
              caption={t('accounting.docsCaption')}
              columns={columns}
              rows={items}
              layout={{ title: 'docNo', status: 'externalRef', subtitle: 'orgUnitId', actions: 'pdf' }}
              onOpen={openDoc}
            />
          )}
          <p className={cn('tabular text-xs text-text-faint')}>
            {t('accounting.docsTotal', { count: list.data?.total ?? items.length })}
          </p>
        </>
      )}

      <DocPreviewDialog
        docId={previewId}
        onClose={() => setPreviewId(null)}
        pdfPending={pdfPendingId === previewId}
        onDownloadPdf={(docNo) => {
          if (previewId !== null) downloadPdf(previewId, docNo);
        }}
      />
    </div>
  );
};
